import { useMemo, useRef, useState, type ChangeEvent } from 'react'
import { MastheadCard } from '@/components/layout/MastheadCard'
import { PAGE_COLUMN } from '@/components/layout/pageLayout'
import { GroupLabel } from '@/components/layout/SectionHeading'
import { EmptyState } from '@/components/layout/EmptyState'
import { Plus } from 'lucide-react'
import { useDocuments, type SymphonyDocument } from '@/hooks/useDocuments'
import { useAuth } from '@/hooks/useAuth'
import { showToast } from '@/hooks/useToast'
import { attachFile, analyzeAttachment, ATTACHMENT_ACCEPT } from '@/lib/taskAttachments'
import { DocumentRow } from './DocumentRow'
import { DocumentProposalRow } from '@/components/surface/sections/DocumentProposal'

function groupByOwner(docs: SymphonyDocument[]): [string, SymphonyDocument[]][] {
  const groups = new Map<string, SymphonyDocument[]>()
  for (const d of docs) {
    const key = d.owner?.trim() || 'Unassigned'
    const bucket = groups.get(key)
    if (bucket) bucket.push(d)
    else groups.set(key, [d])
  }
  return [...groups.entries()].sort(([a], [b]) =>
    a === 'Unassigned' ? 1 : b === 'Unassigned' ? -1 : a.localeCompare(b)
  )
}

export function DocumentsApp() {
  const {
    documents, proposals, isLoading, error,
    keepDocument, dismissDocument, setScope, deleteDocument, updateDocument, reload,
  } = useDocuments()
  const { user } = useAuth()
  const groups = useMemo(() => groupByOwner(documents), [documents])
  const fileInput = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  async function onPick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !user) return
    setUploading(true)
    try {
      // A shelf document has no parent entity, so it hangs off the user
      // themselves — entity_type 'document', entity_id = the user's id.
      const result = await attachFile('document', user.id, file, file.name)
      if (!result) showToast(`Couldn't upload "${file.name}". Please try again.`, 'error')
      if (result && (result.contentType.startsWith('image/') || result.contentType === 'application/pdf')) {
        // Classify it so it arrives already proposed rather than unlabeled.
        await analyzeAttachment(result.id, 'A document filed directly in the Documents shelf')
      }
      await reload()
    } catch {
      // A failed classification leaves the file stored, just unlabeled.
      showToast(`"${file.name}" had a problem while filing. Check the shelf and try again if it's missing.`, 'error')
      await reload()
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className={PAGE_COLUMN}>
      <MastheadCard
        variant="page"
        title="Documents"
        motif="documents"
        subline="Things you'll need again. Private unless you share them."
        footer={
          <button
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
            aria-label="Add a document"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[14px] text-primary-700 hover:bg-primary-50 disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
            {uploading ? 'Uploading…' : 'Add'}
          </button>
        }
      />
      <input
        ref={fileInput}
        type="file"
        accept={ATTACHMENT_ACCEPT}
        className="hidden"
        onChange={(e) => void onPick(e)}
      />

      {error && <div className="mb-4 text-sm text-danger-600">{error}</div>}

      {proposals.length > 0 && (
        <section className="mb-6">
          <GroupLabel>Suggested</GroupLabel>
          {proposals.map((p) => (
            <DocumentProposalRow
              key={p.id}
              kind={p.kind}
              label={p.label}
              onKeep={() => void keepDocument(p.id)}
              onDismiss={() => void dismissDocument(p.id)}
            />
          ))}
        </section>
      )}

      {isLoading ? (
        <p className="py-4 text-[14px] text-neutral-400">Loading…</p>
      ) : documents.length === 0 ? (
        <EmptyState title="No documents yet">
          When you attach something like a license or an insurance card, Symphony will offer
          to keep it here.
        </EmptyState>
      ) : (
        groups.map(([owner, docs]) => (
          <section key={owner} className="mb-6">
            <GroupLabel>{owner}</GroupLabel>
            {docs.map((d) => (
              <DocumentRow
                key={d.id}
                document={d}
                onToggleScope={() => void setScope(d.id, d.scope === 'private' ? 'household' : 'private')}
                onDelete={() => void deleteDocument(d)}
                onSave={(id, edits) => void updateDocument(id, edits)}
              />
            ))}
          </section>
        ))
      )}
    </div>
  )
}
